import { useState, useEffect, useMemo } from "react";
import { ArrowRight, Check } from "lucide-react";
import { Modal } from "../primitives/Modal";
import { Button } from "../primitives/Button";
import { listAmbassadorOrgs, createMembership, updateMembership, type Membership } from "../../api/admin";
import { cn } from "../utils/cn";

type ChangeAmbassadorFlowProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  userId: string;
  currentMemberships: Membership[];
};

export function ChangeAmbassadorFlow({
  open,
  onClose,
  onSuccess,
  userId,
  currentMemberships,
}: ChangeAmbassadorFlowProps) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [ambassadorOrgs, setAmbassadorOrgs] = useState<Array<{ id: string; name: string; city?: string; province?: string }>>([]);
  const [currentAmbassadorMembership, setCurrentAmbassadorMembership] = useState<Membership | null>(null);
  const [newAmbassadorOrgId, setNewAmbassadorOrgId] = useState<string>("");
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter current memberships to only ambassador_org type
  const ambassadorMemberships = useMemo(() => {
    return currentMemberships.filter(
      (m) => m.organization?.type === "ambassador_org" && m.status === "active"
    );
  }, [currentMemberships]);

  useEffect(() => {
    if (open) {
      setStep(1);
      setCurrentAmbassadorMembership(null);
      setNewAmbassadorOrgId("");
      setError(null);
      
      listAmbassadorOrgs()
        .then((data) => {
          setAmbassadorOrgs(data.items || []);
        })
        .catch((error) => {
          console.error("Failed to load ambassador orgs:", error);
          setError("Erreur lors du chargement des ambassadeurs");
        });
    }
  }, [open]);

  const handleStep1Next = () => {
    if (!currentAmbassadorMembership) {
      setError("Veuillez sélectionner le membership ambassadeur actuel");
      return;
    }
    setStep(2);
    setError(null);
  };

  const handleStep2Next = () => {
    if (!newAmbassadorOrgId) {
      setError("Veuillez sélectionner la nouvelle organisation ambassadeur");
      return;
    }
    setStep(3);
    setError(null);
  };

  const handleConfirm = async () => {
    if (!currentAmbassadorMembership || !newAmbassadorOrgId) return;

    setProcessing(true);
    setError(null);

    try {
      // Step 1: Suspend old membership
      await updateMembership(currentAmbassadorMembership.id, { status: "suspended" });

      // Step 2: Create new membership
      await createMembership({
        organization_id: newAmbassadorOrgId,
        user_id: userId,
        org_role: currentAmbassadorMembership.org_role, // Keep same role
        status: "active",
      });

      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors du changement d'ambassadeur");
    } finally {
      setProcessing(false);
    }
  };

  const selectedNewOrg = ambassadorOrgs.find((org) => org.id === newAmbassadorOrgId);

  return (
    <Modal
      title="Changer d'ambassadeur"
      open={open}
      onClose={onClose}
      showDefaultButtons={false}
      showCloseButton={true}
      size="lg"
    >
      <div className="space-y-4">
        {/* Progress indicator */}
        <div className="flex items-center justify-between">
          <div className={cn("flex items-center gap-2", step >= 1 ? "text-primary" : "text-muted")}>
            <div className={cn("w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium", step >= 1 ? "bg-primary text-primary-foreground" : "bg-surface-muted text-muted")}>
              {step > 1 ? <Check size={12} strokeWidth={2} /> : "1"}
            </div>
            <span className="text-xs font-medium">Ancien</span>
          </div>
          <ArrowRight size={16} className={cn("text-muted", step >= 2 && "text-primary")} />
          <div className={cn("flex items-center gap-2", step >= 2 ? "text-primary" : "text-muted")}>
            <div className={cn("w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium", step >= 2 ? "bg-primary text-primary-foreground" : "bg-surface-muted text-muted")}>
              {step > 2 ? <Check size={12} strokeWidth={2} /> : "2"}
            </div>
            <span className="text-xs font-medium">Nouveau</span>
          </div>
          <ArrowRight size={16} className={cn("text-muted", step >= 3 && "text-primary")} />
          <div className={cn("flex items-center gap-2", step >= 3 ? "text-primary" : "text-muted")}>
            <div className={cn("w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium", step >= 3 ? "bg-primary text-primary-foreground" : "bg-surface-muted text-muted")}>
              3
            </div>
            <span className="text-xs font-medium">Confirmer</span>
          </div>
        </div>

        {/* Step 1: Select current ambassador membership */}
        {step === 1 && (
          <div className="space-y-3">
            <div>
              <h3 className="text-sm font-semibold text-text mb-2">Étape 1 : Sélectionner l'ambassadeur actuel</h3>
              <p className="text-xs text-muted mb-3">
                Choisissez le membership ambassadeur que vous souhaitez remplacer.
              </p>
            </div>
            {ambassadorMemberships.length === 0 ? (
              <div className="rounded-md border border-border bg-surface-muted p-4 text-center">
                <div className="text-sm text-muted">Cet utilisateur n'a pas de membership ambassadeur actif.</div>
              </div>
            ) : (
              <div className="space-y-2">
                {ambassadorMemberships.map((membership) => (
                  <button
                    key={membership.id}
                    type="button"
                    onClick={() => setCurrentAmbassadorMembership(membership)}
                    className={cn(
                      "w-full text-left p-3 rounded-md border transition",
                      currentAmbassadorMembership?.id === membership.id
                        ? "border-primary bg-primary/5"
                        : "border-border bg-surface hover:bg-surface-muted"
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium text-text">
                          {membership.organization?.name || "—"}
                        </div>
                        <div className="text-xs text-muted">
                          Rôle: {membership.org_role} • {membership.organization?.city || ""}
                        </div>
                      </div>
                      {currentAmbassadorMembership?.id === membership.id && (
                        <span className="text-primary">✓</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button label="Annuler" intent="default" size="sm" onClick={onClose} />
              <Button
                label="Suivant"
                intent="primary"
                size="sm"
                onClick={handleStep1Next}
                disabled={!currentAmbassadorMembership || ambassadorMemberships.length === 0}
              />
            </div>
          </div>
        )}

        {/* Step 2: Select new ambassador organization */}
        {step === 2 && (
          <div className="space-y-3">
            <div>
              <h3 className="text-sm font-semibold text-text mb-2">Étape 2 : Choisir le nouvel ambassadeur</h3>
              <p className="text-xs text-muted mb-3">
                Sélectionnez la nouvelle organisation ambassadeur pour cet utilisateur.
              </p>
            </div>
            {ambassadorOrgs.length === 0 ? (
              <div className="rounded-md border border-border bg-surface-muted p-4 text-center">
                <div className="text-sm text-muted">Aucune organisation ambassadeur disponible.</div>
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {ambassadorOrgs
                  .filter((org) => org.id !== currentAmbassadorMembership?.organization_id)
                  .map((org) => (
                    <button
                      key={org.id}
                      type="button"
                      onClick={() => setNewAmbassadorOrgId(org.id)}
                      className={cn(
                        "w-full text-left p-3 rounded-md border transition",
                        newAmbassadorOrgId === org.id
                          ? "border-primary bg-primary/5"
                          : "border-border bg-surface hover:bg-surface-muted"
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-medium text-text">{org.name}</div>
                          {(org.city || org.province) && (
                            <div className="text-xs text-muted">
                              {org.city || ""}
                              {org.city && org.province ? ", " : ""}
                              {org.province || ""}
                            </div>
                          )}
                        </div>
                        {newAmbassadorOrgId === org.id && <span className="text-primary">✓</span>}
                      </div>
                    </button>
                  ))}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button label="Précédent" intent="default" size="sm" onClick={() => setStep(1)} />
              <Button
                label="Suivant"
                intent="primary"
                size="sm"
                onClick={handleStep2Next}
                disabled={!newAmbassadorOrgId}
              />
            </div>
          </div>
        )}

        {/* Step 3: Confirm */}
        {step === 3 && (
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-text mb-2">Étape 3 : Confirmer le changement</h3>
              <p className="text-xs text-muted mb-4">
                Vérifiez les détails avant de confirmer le changement d'ambassadeur.
              </p>
            </div>

            <div className="space-y-3">
              <div className="rounded-md border border-warning/20 bg-warning/5 p-3">
                <div className="text-xs font-medium text-text mb-1">Ancien ambassadeur</div>
                <div className="text-sm text-text">
                  {currentAmbassadorMembership?.organization?.name || "—"}
                </div>
                <div className="text-xs text-muted mt-1">
                  Action: Suspendre le membership
                </div>
              </div>

              <div className="rounded-md border border-success/20 bg-success/5 p-3">
                <div className="text-xs font-medium text-text mb-1">Nouvel ambassadeur</div>
                <div className="text-sm text-text">
                  {selectedNewOrg?.name || "—"}
                </div>
                <div className="text-xs text-muted mt-1">
                  Action: Créer un nouveau membership avec le rôle "{currentAmbassadorMembership?.org_role || "ops"}"
                </div>
              </div>
            </div>

            {error && (
              <div className="rounded-md border border-danger bg-danger/10 p-3">
                <div className="text-xs text-danger">{error}</div>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button label="Précédent" intent="default" size="sm" onClick={() => setStep(2)} disabled={processing} />
              <Button
                label="Confirmer le changement"
                intent="primary"
                size="sm"
                onClick={handleConfirm}
                disabled={processing}
              />
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
